<template>
    <div class="border-0 p-0 m-0">
        <div
            :class="inputClasses"
            @click="fileInput?.click()"
            class="cursor-pointer w-full border-2 border-dashed border-stone-200 bg-stone-50 rounded-lg2 h-20 flex flex-col items-center justify-center"
        >
            <div
                v-if="filename"
                class="text-center text-sm font-light overflow-hidden"
            >
                {{ filename }}
            </div>
            <div
                v-else
                class="flex items-center gap-x-2"
            >
                <Icon
                    v-if="iconName"
                    :name="iconName"
                    class="w-6 h-6 text-gray-400"
                />
                <div>Click to select a file</div>
            </div>
        </div>
        <label class="hidden">
            <input
                @change="setFilename"
                v-bind="$attrs"
                :key="inputKey"
                :multiple="maxFiles > 1"
                class="invisible"
                ref="fileInput"
                type="file"
            />
        </label>
    </div>
</template>

<script setup lang="ts">
    import { onBeforeUnmount, ref } from 'vue'
    import Icon from '@/Shared/Component/Icon.vue'

    defineOptions({
        inheritAttrs: false,
    })

    const emits = defineEmits<{
        onPreviewUrlUpdated: [{ value: string | null }]
    }>()

    const props = defineProps({
        inputClasses: {
            type: String,
            default: null,
        },
        maxFiles: {
            type: Number,
            default: 1,
        },
        maxSizeInBytes: {
            type: Number,
            default: 1,
        },
        iconName: {
            type: String,
            default: null,
        },
    })

    const inputKey = ref<number>(1)
    const fileInput = ref<HTMLInputElement | null>(null)
    const filename = ref<string>('')

    const setFilename = (event: Event) => {
        const target = event.target as HTMLInputElement | null

        if (target === null) {
            return
        }

        const files = Array.from(target.files ?? [])

        if (files.length > props.maxFiles) {
            inputKey.value += 1
            filename.value = ''
            clearPreviewUrl()
            emitPreviewUrlUpdated()
            alert(`Please, select at most ${props.maxFiles} files.`)
            return
        }

        let size = 0

        files.forEach((file) => (size += file.size))

        if (size > props.maxSizeInBytes) {
            inputKey.value += 1
            filename.value = ''
            clearPreviewUrl()
            emitPreviewUrlUpdated()
            alert(`File is too big.`)
            return
        }

        clearPreviewUrl()

        if (files.length === 0) {
            filename.value = ''
            emitPreviewUrlUpdated()
            return
        }

        if (files.length === 1) {
            filename.value = files[0].name
            setPreviewUrl(files[0])
            emitPreviewUrlUpdated()
            return
        }

        filename.value = `${files.length} files selected.`
        emitPreviewUrlUpdated()
    }

    // ------------
    // Preview URL event
    // ------------

    const previewUrl = ref<string | null>(null)

    const setPreviewUrl = (file: File): void => {
        previewUrl.value = URL.createObjectURL(file)
    }

    const clearPreviewUrl = () => {
        if (previewUrl.value === null) {
            return
        }

        URL.revokeObjectURL(previewUrl.value)
        previewUrl.value = null
    }

    const emitPreviewUrlUpdated = () => {
        emits('onPreviewUrlUpdated', { value: previewUrl.value })
    }

    // ------------
    // Lifecycle events
    // ------------

    onBeforeUnmount(() => {
        clearPreviewUrl()
    })
</script>
